import { createWebHistory, createRouter } from 'vue-router';
import routes from './routes';
import appConfig from '../../app.config';
import { useAuthStore } from '@/store/auth';
import { storeToRefs } from 'pinia';
import cookieService from '@/services/cookie';

// Constante para ID de administrador
const ADMIN_ROLE_ID = '8e56078d-962e-49be-91c7-d510b4b4879e';

// Função para verificar se o usuário é admin
function isUserAdmin(userData, isAdminComputed) {
  if (!userData) return false;

  const byComputedValue = isAdminComputed;
  const byRoleName = userData?.accountRole?.name === 'ADMIN';
  const byRoleId = userData?.accountRole?.id === ADMIN_ROLE_ID;
  const byAccountRoleId = userData?.accountRoleId === ADMIN_ROLE_ID;

  return byComputedValue || byRoleName || byRoleId || byAccountRoleId;
}

const router = createRouter({
  history: createWebHistory('/'),
  routes,
});

// Verificar autenticação antes de cada navegação
router.beforeEach(async (to, from, next) => {
  // Inicializar o store de autenticação
  const authStore = useAuthStore();
  const { isAuthenticated, isAdmin, userRole, userPermissions } =
    storeToRefs(authStore);

  // Verificar se há um refresh token válido mas o estado não está inicializado
  const hasToken = cookieService.isAuthenticated();

  // Verificar se é necessário atualizar os dados do usuário
  let userDataUpdated = false;

  if (hasToken) {
    try {
      // Atualizar os dados do usuário antes de qualquer verificação de permissão
      userDataUpdated = await authStore.refreshUserData();
    } catch (error) {
      console.error('Erro ao atualizar dados do usuário:', error);
      // Se falhar na atualização e a rota requer autenticação, redirecionar para login
      if (to.matched.some((record) => record.meta.requiresAuth)) {
        cookieService.clearAll();
        next({
          name: 'login',
          query: { redirect: to.fullPath, error: 'auth_refresh_failed' },
        });
        return;
      }
    }
  }

  // Verifica se a rota requer autenticação
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  // Verifica se a rota é apenas para visitantes (não autenticados)
  const guestOnly = to.matched.some((record) => record.meta.guestOnly);
  // Verifica se a rota requer usuário ativo (não guest)
  const requiresActiveUser = to.matched.some(
    (record) => record.meta.requiresActiveUser
  );
  // Verifica se a rota permite acesso no primeiro login (quando mustChangePassword é true)
  const allowFirstAccess = to.matched.some(
    (record) => record.meta.allowFirstAccess
  );

  // Se a rota exige autenticação e o usuário não está autenticado
  if (requiresAuth && !isAuthenticated.value) {
    next({
      name: 'login',
      query: { redirect: to.fullPath },
    });
    return;
  }

  // Se a rota é apenas para visitantes e o usuário está autenticado
  if (guestOnly && isAuthenticated.value) {
    next({ name: 'suppliers' });
    return;
  }

  // Verificar se o usuário precisa trocar a senha e redirecionar para a página de atualização
  if (
    isAuthenticated.value &&
    authStore.user?.mustChangePassword === true &&
    to.name !== 'update-password' &&
    !allowFirstAccess
  ) {
    next({ name: 'update-password' });
    return;
  }

  // Verificação de papel (role) GUEST - redireciona para awaiting-approval
  if (
    isAuthenticated.value &&
    (authStore.user?.role === 'GUEST' || userRole.value === 'GUEST') &&
    !to.meta.allowGuest && // Se a rota não permitir explicitamente GUEST
    to.name !== 'awaiting-approval' &&
    to.name !== 'me' &&
    to.name !== 'me-edit' &&
    to.name !== 'logout' &&
    to.name !== 'update-password'
  ) {
    next({ name: 'awaiting-approval' });
    return;
  }

  // Se a rota requer usuário ativo e o usuário é guest
  if (
    requiresActiveUser &&
    (authStore.user?.role === 'GUEST' || userRole.value === 'GUEST')
  ) {
    next({ name: 'awaiting-approval' });
    return;
  }

  // Verificar se a rota requer permissão de administrador
  if (requiresAuth && isAuthenticated.value && to.meta.requiresAdmin) {
    // Obter dados atualizados do usuário (pode ter mudado após o refreshUserData)
    const updatedUserData = cookieService.getUserData();

    // Verificar se o usuário é administrador de todas as formas possíveis
    const adminState = isUserAdmin(updatedUserData, isAdmin.value);

    if (!adminState) {
      // Se a verificação falhar, mas não atualizamos os dados do usuário, tentar novamente com força
      if (!userDataUpdated) {
        await authStore.refreshUserData();

        // Verificar novamente após a atualização forçada
        const forcedUpdatedUserData = cookieService.getUserData();
        const adminStateAfterForce = isUserAdmin(
          forcedUpdatedUserData,
          isAdmin.value
        );

        if (adminStateAfterForce) {
          next();
          return;
        }
      }

      next({ name: 'access-denied' });
      return;
    }
  }

  // Verificar permissões específicas se necessário
  if (requiresAuth && isAuthenticated.value && to.meta.requiredPermission) {
    // Obter dados atualizados do usuário
    const updatedUserData = cookieService.getUserData();

    // Verificar se é administrador usando a função utilitária
    const adminState = isUserAdmin(updatedUserData, isAdmin.value);

    // Verifica se é administrador (acesso total)
    if (adminState) {
      next();
      return;
    }

    // Verifica permissão específica
    const hasRequiredPermission = userPermissions.value.includes(
      to.meta.requiredPermission
    );
    if (!hasRequiredPermission) {
      next({ name: 'access-denied' });
      return;
    }
  }

  // Verificar função específica se necessário
  if (requiresAuth && isAuthenticated.value && to.meta.requiredRole) {
    // Obter dados atualizados do usuário
    const updatedUserData = cookieService.getUserData();

    // Verificar se é administrador usando a função utilitária
    const adminState = isUserAdmin(updatedUserData, isAdmin.value);

    // Administradores têm acesso a todas as rotas
    if (adminState) {
      next();
      return;
    }

    const requiredRole = to.meta.requiredRole;

    // Se for um array de funções, verifica se o usuário tem alguma delas
    let hasRequiredRole = false;
    if (Array.isArray(requiredRole)) {
      hasRequiredRole = requiredRole.includes(userRole.value);
    }
    // Se for uma string, verifica se o usuário tem exatamente essa função
    else {
      hasRequiredRole = userRole.value === requiredRole;
    }

    if (!hasRequiredRole) {
      next({ name: 'access-denied' });
      return;
    }
  }

  // Se tudo estiver ok, continua a navegação
  next();
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args);
              reject(new Error('Redirected'));
            } else {
              resolve();
            }
          });
        } else {
          // Otherwise, continue resolving the route.
          resolve();
        }
      });
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return;
  }

  // Definir título da página
  const title = routeTo.meta.title || 'Página';
  document.title = title + ' | ' + appConfig.title;

  // If we reach this point, continue resolving the route.
  next();
});

export default router;
